import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditProfileDialog } from '@/components/profile/EditProfileDialog';
import { MembershipCard, MembershipCardSkeleton } from '@/components/profile/MembershipCard';
import { PhysicianCard, PhysicianCardSkeleton } from '@/components/profile/PhysicianCard';
import { PracticeInfoEmpty } from '@/components/profile/PracticeInfoEmpty';
import { PracticeInfoError } from '@/components/profile/PracticeInfoError';
import { PracticeOfficeCard, PracticeOfficeCardSkeleton } from '@/components/profile/PracticeOfficeCard';
import {
  ProfileDetailsCard,
  ProfileDetailsCardError,
  ProfileDetailsCardSkeleton,
} from '@/components/profile/ProfileDetailsCard';
import { ProfileOverviewCard, ProfileOverviewCardSkeleton } from '@/components/profile/ProfileOverviewCard';
import { UpdateProfilePhotoDialog } from '@/components/profile/UpdateProfilePhotoDialog';
import { usePracticeInfo } from '@/hooks/practice/usePracticeInfo';
import { useMyProfile } from '@/hooks/profile/useMyProfile';
import { useSaveProfilePhoto } from '@/hooks/profile/useSaveProfilePhoto';

/**
 * One `myPracticeInfo` query serves all three cards, so they load, fail and succeed together — and
 * independently of `useMyProfile`, so a slow read on either never blocks the other column.
 */
function PracticeRailCards() {
  const { data, isPending, isError, error } = usePracticeInfo();

  if (isPending) {
    return (
      <>
        <PhysicianCardSkeleton />
        <PracticeOfficeCardSkeleton />
        <MembershipCardSkeleton />
      </>
    );
  }
  if (isError) {
    // NOT_FOUND means the member has no assigned practice on file — a reachable, calm state, not a
    // failure. Any other code is a genuine error.
    return extractGqlErrorCode(error) === 'NOT_FOUND' ? <PracticeInfoEmpty /> : <PracticeInfoError error={error} />;
  }
  if (data) {
    return (
      <>
        <PhysicianCard practice={data} />
        <PracticeOfficeCard practice={data} />
        <MembershipCard />
      </>
    );
  }
  return null;
}

function PracticeRail() {
  return (
    <div className="flex flex-col gap-6">
      <PracticeRailCards />
    </div>
  );
}

export function ProfilePage() {
  const { t } = useTranslation('members');
  const { data, isPending, isError, error } = useMyProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const savePhoto = useSaveProfilePhoto();

  return (
    <>
      <div className="mb-6">
        <h1 className="text-h1 font-semibold">{t('profile.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('profile.subtitle')}</p>
      </div>
      {/* @container lives on its own wrapper, separate from the grid that reads @3xl: off it —
          a container query can't resolve against the element declaring it. Below @3xl the rail
          drops under the details column, matching the design's Direction D layout. */}
      <div className="@container">
        <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            {isPending && <ProfileDetailsCardSkeleton />}
            {!isPending && isError && <ProfileDetailsCardError error={error} />}
            {!isPending && !isError && data && (
              <>
                <ProfileDetailsCard member={data} onEdit={() => setIsEditing(true)} />
                <EditProfileDialog open={isEditing} member={data} onClose={() => setIsEditing(false)} />
              </>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {isPending && <ProfileOverviewCardSkeleton />}
            {!isPending && !isError && data && (
              <ProfileOverviewCard member={data} onEditPhoto={() => setIsEditingPhoto(true)} />
            )}
            <PracticeRail />
          </div>
        </div>
      </div>
      <UpdateProfilePhotoDialog
        open={isEditingPhoto}
        onClose={() => setIsEditingPhoto(false)}
        onSave={(photo) => savePhoto.mutateAsync(photo)}
        isSaving={savePhoto.isPending}
      />
    </>
  );
}
